/**
 * MissionControl — the Vault's full populated layout.
 *
 * Two columns on ≥lg:
 *   - Main:    GI grid (mission stations)
 *   - Aside:   Recent activity
 *
 * One-column stacked on narrower viewports.
 *
 * The main column's header carries the primary CTAs: Spark, Verify,
 * and (in dev only) a demo-reset control. `+Spark a GI` is always
 * the most visually prominent.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import type { VaultSnapshot } from '@/api/types';

import { GiCard } from './GiCard';
import { RecentActivity } from './RecentActivity';
import { StatsBar } from './StatsBar';

export function MissionControl({
  snapshot,
  onResetDemo,
}: {
  snapshot: VaultSnapshot;
  onResetDemo?: () => void;
}) {
  const { t } = useTranslation('vault');

  return (
    <div className="flex flex-col gap-6">
      <StatsBar stats={snapshot.stats} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        {/* Main column */}
        <section aria-labelledby="your-gis-heading" className="flex flex-col gap-4">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <h2
              id="your-gis-heading"
              className="text-sm font-semibold uppercase tracking-wider text-ink-muted"
            >
              {t('sections.your_gis')}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/verify" className="contents">
                <Button variant="secondary" size="sm">
                  {t('cta.verify')}
                </Button>
              </Link>
              <Link to="/spark" className="contents">
                <Button variant="primary" size="sm" leading={<SparkGlyph />}>
                  {t('cta.spark')}
                </Button>
              </Link>
              {onResetDemo ? (
                <Button variant="ghost" size="sm" onClick={onResetDemo}>
                  {t('cta.reset')}
                </Button>
              ) : null}
            </div>
          </header>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {snapshot.gis.map((gi) => (
              <GiCard key={gi.root_id} gi={gi} />
            ))}
          </div>
        </section>

        {/* Sidebar */}
        <aside aria-labelledby="activity-heading" className="flex flex-col gap-4">
          <h2
            id="activity-heading"
            className="text-sm font-semibold uppercase tracking-wider text-ink-muted"
          >
            {t('sections.recent_activity')}
          </h2>
          <RecentActivity events={snapshot.activity} />
        </aside>
      </div>
    </div>
  );
}

function SparkGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}
